import { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import gsap from 'gsap';
import { useApp } from '../context/AppContext';
import { formatNaira, abbreviateState } from '../utils/formatters';
import { getTopNStates } from '../utils/dataLoader';
import { getStateColor } from '../utils/colors';

export function TopNBarChart() {
  const { state, data, currentYearSummary, selectState, setHoveredState, dispatch } = useApp();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);
  const prevTopNRef = useRef<number>(state.topN);
  
  // Get top N states for current year
  const topStates = useMemo(() => {
    if (!currentYearSummary) return [];
    return getTopNStates(currentYearSummary, state.topN);
  }, [currentYearSummary, state.topN]);
  
  // D3 rendering with smooth transitions
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || topStates.length === 0) return;
    
    const svg = d3.select(svgRef.current);
    const containerWidth = containerRef.current.clientWidth;
    
    const margin = { top: 10, right: 90, bottom: 10, left: 90 };
    const width = containerWidth - margin.left - margin.right;
    const barHeight = 28;
    const gap = 6;
    const chartHeight = (barHeight + gap) * topStates.length;
    
    svg.attr('width', containerWidth).attr('height', chartHeight + margin.top + margin.bottom);
    
    // Check if topN changed - if so, we need to reinitialize
    const topNChanged = prevTopNRef.current !== state.topN;
    prevTopNRef.current = state.topN;
    
    // Scales
    const xScale = d3
      .scaleLinear()
      .domain([0, d3.max(topStates, (d) => d.total) ?? 0])
      .range([0, width]);
    
    const yScale = d3
      .scaleBand()
      .domain(topStates.map((d) => d.state))
      .range([0, chartHeight])
      .padding(0.15);
    
    // Check if this is initial render or topN changed
    if (!isInitializedRef.current || topNChanged) {
      // Clear previous content for initial render
      svg.selectAll('*').remove();
      
      const defs = svg.append('defs');
      
      // Create main group
      svg.append('g')
        .attr('class', 'chart-group')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Create gradients
    topStates.forEach((d) => {
      const isSelected = state.selectedStates.includes(d.state);
      const gradient = defs
          .append('linearGradient')
          .attr('id', `bar-gradient-${d.state.replace(/\s+/g, '-')}`)
          .attr('x1', '0%')
          .attr('y1', '0%')
          .attr('x2', '100%')
          .attr('y2', '0%');
        
        if (isSelected) {
          const color = getStateColor(state.selectedStates.indexOf(d.state));
          gradient.append('stop').attr('offset', '0%').attr('stop-color', color);
          gradient.append('stop').attr('offset', '100%').attr('stop-color', d3.color(color)?.brighter(0.5)?.formatHex() || color);
        } else {
          gradient.append('stop').attr('offset', '0%').attr('stop-color', '#0891b2');
          gradient.append('stop').attr('offset', '100%').attr('stop-color', '#22d3ee');
        }
      });
      
      isInitializedRef.current = true;
    }
    
    const g = svg.select('.chart-group');
    
    // DATA JOIN - bind data with state as key
    const barGroups = g
      .selectAll<SVGGElement, typeof topStates[0]>('.bar-group')
      .data(topStates, (d) => d.state);
    
    // EXIT - remove bars that are no longer in top N
    barGroups.exit()
      .transition()
      .duration(400)
      .style('opacity', 0)
      .attr('transform', `translate(0,${chartHeight + 50})`)
      .remove();
    
    // ENTER - create new bar groups
    const enterGroups = barGroups.enter()
      .append('g')
      .attr('class', 'bar-group')
      .attr('transform', (d) => `translate(0,${yScale(d.state)})`)
      .style('cursor', 'pointer')
      .style('opacity', 0);
    
    // Background track for new bars
    enterGroups
      .append('rect')
      .attr('class', 'bar-bg')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', yScale.bandwidth())
      .attr('fill', '#1e293b')
      .attr('rx', 4);
    
    // Value bar for new entries
    enterGroups
      .append('rect')
      .attr('class', 'bar-fg')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 0)
      .attr('height', yScale.bandwidth())
      .attr('rx', 4);
    
    // Rank label for new entries
    enterGroups
      .append('text')
      .attr('class', 'rank-label')
      .attr('x', -8)
      .attr('y', yScale.bandwidth() / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#64748b')
      .attr('font-size', '11px')
      .attr('font-weight', '600');
    
    // State name for new entries
    enterGroups
      .append('text')
      .attr('class', 'state-label')
      .attr('x', -45)
      .attr('y', yScale.bandwidth() / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '600');
    
    // Value label for new entries
    enterGroups
      .append('text')
      .attr('class', 'value-label')
      .attr('x', width + 8)
      .attr('y', yScale.bandwidth() / 2)
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#67e8f9')
      .attr('font-size', '11px')
      .attr('font-family', 'JetBrains Mono, monospace');
    
    // MERGE - combine enter and update selections
    const allGroups = enterGroups.merge(barGroups);
    
    // Set up event handlers on all groups
    allGroups
      .on('click', (_event, d) => selectState(d.state))
      .on('mouseenter', function(_event, d) {
        setHoveredState(d.state);
        d3.select(this).select('.bar-fg').attr('opacity', 0.85);
        d3.select(this).select('.state-label').attr('fill', '#22d3ee');
      })
      .on('mouseleave', function(_event, stateData) {
        setHoveredState(null);
        d3.select(this).select('.bar-fg').attr('opacity', 1);
        d3.select(this).select('.state-label').attr('fill', 
          state.selectedStates.includes(stateData.state) ? '#22d3ee' : '#e2e8f0'
        );
      });
    
    // Animate entrance of new bars
    enterGroups
      .transition()
      .duration(500)
      .ease(d3.easeCubicOut)
      .style('opacity', 1);
    
    // UPDATE positions with smooth transition using GSAP for elastic effect
    allGroups.each(function(d) {
      const group = this as SVGGElement;
      const targetY = yScale(d.state) ?? 0;
      
      gsap.to(group, {
        y: targetY,
        duration: 0.6,
        ease: 'elastic.out(1, 0.75)',
      });
    });
    
    // Update gradients for selection state
    const defs = svg.select('defs');
    topStates.forEach((d) => {
      const gradientId = `bar-gradient-${d.state.replace(/\s+/g, '-')}`;
      const existingGradient = defs.select<SVGLinearGradientElement>(`#${gradientId}`);
      
      let gradient: d3.Selection<SVGLinearGradientElement, unknown, null, undefined>;
      
      if (existingGradient.empty()) {
        gradient = defs
          .append<SVGLinearGradientElement>('linearGradient')
          .attr('id', gradientId)
          .attr('x1', '0%')
          .attr('y1', '0%')
          .attr('x2', '100%')
          .attr('y2', '0%');
      } else {
        gradient = existingGradient;
      }
      
      gradient.selectAll('stop').remove();
      
      const isSelected = state.selectedStates.includes(d.state);
      if (isSelected) {
        const color = getStateColor(state.selectedStates.indexOf(d.state));
        gradient.append('stop').attr('offset', '0%').attr('stop-color', color);
        gradient.append('stop').attr('offset', '100%').attr('stop-color', d3.color(color)?.brighter(0.5)?.formatHex() || color);
      } else {
        gradient.append('stop').attr('offset', '0%').attr('stop-color', '#0891b2');
        gradient.append('stop').attr('offset', '100%').attr('stop-color', '#22d3ee');
      }
    });
    
    // Update all elements in bar groups
    allGroups.each(function(d) {
      const group = d3.select(this);
      
      // Update bar width with animation
      group.select('.bar-fg')
        .attr('fill', `url(#bar-gradient-${d.state.replace(/\s+/g, '-')})`)
        .transition()
        .duration(600)
        .ease(d3.easeCubicOut)
        .attr('width', xScale(d.total));
      
      // Update rank label
      group.select('.rank-label')
        .text(`#${d.rank}`);
      
      // Update state label
      group.select('.state-label')
        .attr('fill', state.selectedStates.includes(d.state) ? '#22d3ee' : '#e2e8f0')
        .text(abbreviateState(d.state));
      
      // Update value label with number animation
      const valueLabel = group.select('.value-label');
      const currentText = valueLabel.text();
      const currentValue = parseFloat(currentText.replace(/[₦,TB]/g, '')) || 0;
      const targetValue = d.total;
      
      // Animate the number change
      gsap.to({ value: currentValue }, {
        value: targetValue,
        duration: 0.6,
        ease: 'power2.out',
        onUpdate: function() {
          valueLabel.text(formatNaira(this.targets()[0].value, true));
        },
      });
    });
    
  }, [topStates, state.selectedStates, state.topN, selectState, setHoveredState]);
  
  if (!data) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-pulse text-surface-400">Loading chart...</div>
      </div>
    );
  }
  
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display font-semibold text-surface-100">
          Top {state.topN} States
        </h3>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-500">Show:</span>
          {[5, 10, 15].map((n) => (
            <button
              key={n}
              onClick={() => dispatch({ type: 'SET_TOP_N', payload: n })}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                state.topN === n
                  ? 'bg-accent-500/20 text-accent-400'
                  : 'text-surface-400 hover:text-surface-200'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      
      <div ref={containerRef} className="w-full overflow-hidden">
        <svg ref={svgRef} className="w-full" />
      </div>
      
      <p className="text-xs text-surface-500 mt-3 text-center">
        Click a bar to view state details
      </p>
    </div>
  );
}

// Alias for backwards compatibility
export const TopNBarChartWithDispatch = TopNBarChart;
